import { Component, OnInit } from '@angular/core';
import { Result } from 'app/core/models/result';
import { Router } from '@angular/router';
import { User } from 'app/shared/models/User';
import { IHeaderTitle } from 'app/shared/models/header-title';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { CompanyAuditService } from 'app/company-audit/services/company-audit.service';
import { AuditTemplateService } from 'app/audit-template/services/audit-template.service';
import { SharedService } from 'app/shared/services/shared.service';
import { AuditTemplateDetailsModel } from 'app/audit-template/models/audit-template-details.model';
import { CompanyAuditAddModel } from 'app/company-audit/models/company-audit-add.model';
import { CompanyUserModel } from 'app/shared/models/company-user.model';
import { CompanyService } from '../../../company/services/company.service';
import { CompanyUserListModel } from '../../../company/models/company-user-list-model';
import { CompanyDetailsModel } from '../../../company/models/company-details-model';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-company-audit-add',
  templateUrl: './company-audit-add.component.html',
  styleUrls: ['./company-audit-add.component.css']
})
export class CompanyAuditAddComponent implements OnInit {
    headerTitle: IHeaderTitle;
    errors: string[];
    audits: CompanyAuditAddModel[];
    submitAttempt: boolean = false;
    addAuditFormGroup: FormGroup;
    companyId: string;
    companyName: string;
    user: User;
    isAdmin: boolean = false;
    auditTemplates: AuditTemplateDetailsModel[];
    companyUserList: CompanyUserModel[];
    selectedCompany: CompanyUserModel;
    companyList: CompanyDetailsModel[];
    selectedCompanyDetails: CompanyDetailsModel;
    companyUsersList: CompanyUserListModel[];
    selectedAuditTemplate: string;
    showForm: boolean = false;
    showNoAuditAlert = false;
    cId: string = "none";

    constructor(
        private companyAuditService: CompanyAuditService,
        private companyService: CompanyService,
        private auditTemplateService: AuditTemplateService,
        private sharedService: SharedService,
        private fb: FormBuilder,
        private router: Router
    ) { }

    ngOnInit() {

        //this.companyId = this.activatedRoute.snapshot.params['id'];

        firstValueFrom(this.sharedService.currentUser).then(user => {
            this.user = user;
            this.isAdmin = this.user.isAdmin;

            firstValueFrom(this.sharedService.currentCompanyUser).then(companyUser => {
                this.companyUserList = companyUser;

                // Gets company details list of any companies user is a manager of including child companies
                firstValueFrom(this.companyService.getAllCompaniesForUser()).then(result => {
                    this.companyList = result;
                    if (this.companyList.length == 1) {
                        // there is only 1 company, so set it as the selected company, don't show the company selector panel
                        this.companyId = this.companyList[0].id;
                        this.showForm = true;
                        this.loadData();
                    }
                });
            });

        });


    }

    async loadData(): Promise<void> {
        if (this.isAdmin) {
            this.headerTitle = {
                title: 'New Audit',
                breadcrumbs: [
                    { title: 'Company Audits', url: ['/company-audit'] }
                ]
            };
        } else {
            this.headerTitle = {
                title: 'New Audit'
            };
        }
        this.selectedCompany = this.companyUserList.find(x => x.companyId == this.companyId);
        this.selectedCompanyDetails = this.companyList.find(x => x.id == this.companyId);
        this.companyName = this.selectedCompanyDetails.tradingName || this.selectedCompanyDetails.legalName;
        if (this.selectedCompany.userTypeId == 2) {
            // user is a manager
            // use assigned company Id
            await this.getCompanyUsers(this.companyId);
        } else {
            // user is a staff member of only one company
            let thisUser: CompanyUserListModel[] = [{
                id: this.selectedCompany.companyUserId,
                siteUserId: this.selectedCompany.siteUserId,
                firstName: this.selectedCompany.firstName,
                lastName: this.selectedCompany.lastName,
                email: this.selectedCompany.email,
                typeId: this.selectedCompany.userTypeId,
                typeName: this.selectedCompany.userTypeDescription
            }]
            this.companyUsersList = thisUser;
        }
        this.getAuditTemplates(this.selectedCompanyDetails.id);
    }

    async getAuditTemplates(companyId): Promise<void> {
        this.auditTemplates = await firstValueFrom(this.auditTemplateService.getAllForCompany(companyId));

        if (this.auditTemplates.length == 0) {
            this.showNoAuditAlert = true;
        }
        this.initializeForm();
    }

    initializeForm(): void {
        this.addAuditFormGroup = this.fb.group(
            {
                title: ['', Validators.required],
                companyId: [this.companyId],
                auditTemplateId: ['', Validators.required],
                startDate: [new Date(), Validators.required],
                deadlineDate: [''],
                auditStatus: [0],
                completedDate: [null],
                auditorSiteUserId: [this.selectedCompany.siteUserId, Validators.required],
                isExternal: [false],
                reviewerSummary: [''],
                reviewerSiteUserId: [null],
                emailMessage: ['']
            }
        );
    }

    createAudit(): void {
        this.submitAttempt = true;
        this.addAuditFormGroup.markAllAsTouched();

        var startDate = this.addAuditFormGroup.get('startDate').value;
        var deadlineDate = this.addAuditFormGroup.get('deadlineDate').value;
        if (this.addAuditFormGroup.valid && (!deadlineDate || startDate <= deadlineDate)) {
            const formValue = this.addAuditFormGroup.value;

            formValue.startDate = startDate ? new Date(new Date(startDate).setHours(12)).toISOString() : null;
            formValue.deadlineDate = deadlineDate ? new Date(new Date(deadlineDate).setHours(12)).toISOString() : null;

            formValue.siteUserId = this.user.id;
            this.companyAuditService.create(formValue).subscribe(
                auditId => {
                    this.router.navigate(['./company-audit/', auditId]);
                },
                error => {
                    this.errors = (<Result>error).errorList;
                });
        }
    }

    setCompany(cId): void {
        this.companyId = cId;
        this.showForm = true;
        this.loadData();
    }

    unSetCompany(): void {
        this.companyId = "none";
        this.showForm = false;
        this.showNoAuditAlert = false;
    }

    async getCompanyUsers(companyId): Promise<void> {
        this.companyUsersList = await firstValueFrom(this.companyService.getUsers(companyId));
    }

    get title() { return this.addAuditFormGroup.get('title'); }
    get auditTemplateId() { return this.addAuditFormGroup.get('auditTemplateId'); }
    get startDate() { return this.addAuditFormGroup.get('startDate'); }
    get deadlineDate() { return this.addAuditFormGroup.get('deadlineDate'); }
}
